import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, Dialog, DialogHeader, DialogBody, Input, Typography, Button as MaterialButton } from "@material-tailwind/react";
import Layout from "../components/layout/Layout";
import PropertySelector from "../components/PropertySelector";
import DiagnosticsContent from "../components/DiagnosticsContent";
import PrintModal from "../components/modals/PrintModal";
import PaymentModal from "../components/modals/PaymentModal";
import SendModal from "../components/modals/SendModal";
import ShareAccessModal from "../components/modals/ShareAccessModal";
import DownloadModal from "../components/modals/DownloadModal";
import TipModal from "../components/modals/TipModal";
import TipSuccessModal from "../components/modals/TipSuccessModal";
import TipErrorModal from "../components/modals/TipErrorModal";
import axiosInstance from '../utils/axiosInstance';
import { Helmet } from 'react-helmet';
import { useProperties } from "../contexts/PropertiesContext";
import RecommendedDeals from "../components/deals/RecommendedDeals";
import { trackEvent } from "../utils/analytics";
import { createRoot } from 'react-dom/client';
import { colors } from "../utils/colors";

function LoadingSkeleton() {
  return (
    <div className="mb-6">
      {/* Première carte - Informations du diagnostic */}
      <Card className="p-6 shadow-md mb-6">
        <div className="space-y-6 animate-pulse">
          {/* Titre */}
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>

          {/* Grille d'informations */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Première colonne - Dossier/Nom/Type */}
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={`col1-${i}`} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>

            {/* Deuxième colonne - Date/Superficie/Année */}
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={`col2-${i}`} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>

            {/* Troisième colonne - Adresse/Diagnostics */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                  <div className="flex flex-wrap gap-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={`diag-${i}`} className="h-4 bg-gray-200 rounded w-20"></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Structure en colonnes sur desktop */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Colonne gauche - Rapport et Bons plans */}
        <div className="lg:w-3/5 flex flex-col gap-6 order-1">
          {/* Bloc Rapport de diagnostics */}
          <Card className="p-6 shadow-md w-full">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-gray-200"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="flex gap-4">
                  <div className="h-10 bg-gray-200 rounded w-32"></div>
                  <div className="h-10 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          </Card>

          {/* Bloc Amélioration DPE (mobile) */}
          <Card className="p-6 shadow-md flex flex-col lg:hidden order-2">
            <div className="animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-2/5 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="aspect-[4/3] bg-gray-200 rounded mb-6"></div>
              <div className="flex justify-center">
                <div className="h-10 bg-gray-200 rounded w-40"></div>
              </div>
            </div>
          </Card>

          {/* Bloc Bons plans */}
          <div className="order-3 w-full -mb-6 lg:mb-0">
            <Card className="p-6 shadow-md">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-2/5 mb-6"></div>
                <div className="flex gap-4 overflow-x-hidden">
                  {[...Array(3)].map((_, i) => (
                    <div key={`deal-${i}`} className="flex-shrink-0 w-[280px]">
                      <div className="aspect-[4/3] bg-gray-200 rounded mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-10 bg-gray-200 rounded w-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Colonne droite - Amélioration DPE (desktop) */}
        <Card className="lg:w-2/5 p-6 shadow-md hidden lg:flex flex-col order-2">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-2/5 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="aspect-[4/3] bg-gray-200 rounded mb-6"></div>
            <div className="flex justify-center">
              <div className="h-10 bg-gray-200 rounded w-40"></div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default function Diagnostics() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { properties, technicians, loading: propertiesLoading, refreshProperties } = useProperties();
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isShareAccessModalOpen, setIsShareAccessModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const [isTipSuccessModalOpen, setIsTipSuccessModalOpen] = useState(false);
  const [isTipErrorModalOpen, setIsTipErrorModalOpen] = useState(false);
  const [documents, setDocuments] = useState(null);
  const [documentsWithoutInvoice, setDocumentsWithoutInvoice] = useState(null);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [isChangingProperty, setIsChangingProperty] = useState(false);
  const [tipAmount, setTipAmount] = useState(null);
  const recommendedDealsRef = useRef(null);
  const rootRef = useRef(null);

  // États pour le type d'utilisateur
  const [isPro, setIsPro] = useState(false);
  const [isIndependent, setIsIndependent] = useState(false);

  // États pour le modal Little Worker
  const [openLittleWorkerModal, setOpenLittleWorkerModal] = useState(false);
  const [selectedLittleWorkerDeal, setSelectedLittleWorkerDeal] = useState(null); // Bien que nous ouvrions pour un deal spécifique, gardons la structure
  const [ademeNumber, setAdemeNumber] = useState('');
  const [ademeNumberError, setAdemeNumberError] = useState('');

  useEffect(() => {
    const token = document.cookie.split('; ').find(row => row.startsWith('authToken='));
    if (token) {
      const tokenValue = token.split('=')[1];
      try {
        const decodedToken = JSON.parse(atob(tokenValue.split('.')[1]));
        setIsPro(decodedToken.is_organization || decodedToken.is_key_account || false);
        setIsIndependent(decodedToken.is_independent || false);
      } catch (error) {
        console.error('Erreur lors du décodage du token:', error);
      }
    }
  }, []);

  useEffect(() => {
    const injectRecommendedDeals = () => {
      const container = document.getElementById('recommended-deals-container');
      if (container && !rootRef.current) {
        const dealsElement = document.createElement('div');
        container.appendChild(dealsElement);
        
        rootRef.current = createRoot(dealsElement);
        rootRef.current.render(<RecommendedDeals source="diagnostics" />);
        
        recommendedDealsRef.current = dealsElement;
      }
    };

    // Essayer d'injecter immédiatement, puis à intervalles réguliers
    // jusqu'à ce que le conteneur soit disponible
    const intervalId = setInterval(() => {
      injectRecommendedDeals();
    }, 200);

    // Nettoyer après 5 secondes pour éviter des tentatives infinies
    const timeoutId = setTimeout(() => {
      clearInterval(intervalId);
    }, 5000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      
      if (rootRef.current) {
        rootRef.current.unmount();
        rootRef.current = null;
      }
    };
  }, [selectedProperty]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    
    // Vérifier si un pourboire a été effectué avec succès
    if (searchParams.has('tip_success') && searchParams.has('amount')) {
      const amount = searchParams.get('amount');
      setTipAmount(amount);
      setIsTipSuccessModalOpen(true);
      
      // Tracker l'événement
      trackEvent('tip_success_redirect', {
        propertyId: id,
        amount: amount
      });
      
      // Nettoyer l'URL pour éviter de réafficher le modal lors des rechargements
      navigate(`/diagnostics/${id}`, { replace: true });
    }
    
    // Vérifier s'il y a eu une erreur avec le pourboire
    if (searchParams.has('tip_error')) {
      setIsTipErrorModalOpen(true);
      
      // Tracker l'événement
      trackEvent('tip_error_redirect', {
        propertyId: id
      });
      
      // Nettoyer l'URL pour éviter de réafficher le modal lors des rechargements
      navigate(`/diagnostics/${id}`, { replace: true });
    }
  }, [location, navigate, id]);

  useEffect(() => {
    if (!propertiesLoading && !isChangingProperty) {
      if (id) {
        const propertyFromSummary = properties.find(prop => prop.id.toString() === id);
        if (propertyFromSummary) {
          setSelectedProperty(propertyFromSummary);
          
          // Tracking de la sélection d'une propriété spécifique
          trackEvent('diagnostic_view_property', {
            propertyId: propertyFromSummary.id
          });
        } else {
          navigate("/diagnostics", { replace: true });
          
          // Tracking de la redirection (propriété non trouvée)
          trackEvent('diagnostic_redirect_property_not_found', {
            attemptedId: id
          });
        }
      } else if (properties.length > 0) {
        navigate(`/diagnostics/${properties[0].id}`, { replace: true });
        
        // Tracking de la redirection automatique vers la première propriété
        trackEvent('diagnostic_auto_select_property', {
          propertyId: properties[0].id
        });
      }
    }
  }, [id, properties, propertiesLoading, isChangingProperty, navigate]);

  useEffect(() => {
    let isCancelled = false;

    const fetchDocuments = async () => {
      if (!selectedProperty || isChangingProperty) return;

      setIsLoadingDocuments(true);
      
      trackEvent('diagnostic_load_documents', {
        propertyId: selectedProperty.id
      });

      try {
        const response = await axiosInstance.get(`/documents/${selectedProperty.id}`);
        if (!isCancelled) {
          setDocuments(response.data.documents || []);
          const documentsWithoutInvoice = response.data.documents.filter(doc => doc.type !== 'invoice');
          setDocumentsWithoutInvoice(documentsWithoutInvoice);
          
          trackEvent('diagnostic_documents_loaded', {
            propertyId: selectedProperty.id,
            documentCount: response.data.documents?.length || 0
          });
        }
      } catch (error) {
        if (!isCancelled) {
          if (error.response && error.response.status === 404) {
            setDocuments([]);
            setDocumentsWithoutInvoice([]);
          } else {
            console.error('Erreur lors de la récupération des documents:', error);
            
            trackEvent('diagnostic_documents_error', {
              propertyId: selectedProperty.id,
              error: error.message || 'unknown'
            });
          }
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingDocuments(false);
        }
      }
    };

    fetchDocuments();

    return () => {
      isCancelled = true;
    };
  }, [selectedProperty?.id, isChangingProperty, selectedProperty]);

  const handlePrint = (printData) => {
    trackEvent('diagnostic_print', {
      propertyId: selectedProperty?.id,
      documentTypes: printData?.documentTypes || []
    });
    
    console.log("Print data:", printData);
  };

  const handleSend = (sendData) => {
    trackEvent('diagnostic_send', {
      propertyId: selectedProperty?.id,
      recipientCount: sendData?.recipients?.length || 0
    });
    
    console.log("Send data:", sendData);
  };

  const handleShareAccess = (shareData) => {
    trackEvent('diagnostic_share_access', {
      propertyId: selectedProperty?.id,
      recipientEmail: shareData?.email ? 'provided' : 'not_provided'
    });
    
    console.log("Share access data:", shareData);
  };

  const handlePropertyChange = (newPropertyId) => {
    if (!propertiesLoading && selectedProperty && newPropertyId !== selectedProperty.id) {
      trackEvent('diagnostic_change_property', {
        fromPropertyId: selectedProperty.id,
        toPropertyId: newPropertyId
      });
      
      setIsChangingProperty(true);
      // Réinitialiser les documents avant de changer de propriété
      setDocuments(null);
      setDocumentsWithoutInvoice(null);
      setIsLoadingDocuments(true);
      
      navigate(`/diagnostics/${newPropertyId}`, { replace: true });
      
      // Attendre que la navigation soit terminée avant de désactiver le loading
      setTimeout(() => {
        setIsChangingProperty(false);
      }, 400);
    }
  };

  const handleCannotProvideDocument = async (type) => {
    trackEvent('diagnostic_cannot_provide_document', {
      propertyId: selectedProperty?.id,
      documentType: type
    });
    
    try {
      await axiosInstance.post(`/properties/${selectedProperty.id}/cannot-provide`, {
        type: type
      });
      refreshProperties();
    } catch (error) {
      console.error('Erreur lors de la notification de document non fourni:', error);
      
      trackEvent('diagnostic_cannot_provide_error', {
        propertyId: selectedProperty?.id,
        documentType: type,
        error: error.message || 'unknown'
      });
    }
  };

  const handleOpenPrintModal = () => {
    trackEvent('diagnostic_open_print_modal', { propertyId: selectedProperty?.id });
    setIsPrintModalOpen(true);
  };

  const handleOpenPaymentModal = () => {
    trackEvent('diagnostic_open_payment_modal', { propertyId: selectedProperty?.id });
    setIsPaymentModalOpen(true);
  };

  const handleOpenSendModal = () => {
    trackEvent('diagnostic_open_send_modal', { propertyId: selectedProperty?.id });
    setIsSendModalOpen(true);
  };

  const handleOpenShareAccessModal = () => {
    trackEvent('diagnostic_open_share_modal', { propertyId: selectedProperty?.id });
    setIsShareAccessModalOpen(true);
  };

  const handleOpenDownloadModal = () => {
    trackEvent('diagnostic_open_download_modal', { propertyId: selectedProperty?.id });
    setIsDownloadModalOpen(true);
  };

  const handleNavigateToSupport = () => {
    trackEvent('diagnostic_navigate_to_support', { propertyId: selectedProperty?.id });
    navigate("/support/" + selectedProperty.id);
  };

  const handleOpenTipModal = () => {
    trackEvent('diagnostic_open_tip_modal', { propertyId: selectedProperty?.id });
    setIsTipModalOpen(true);
  };

  const handleOpenLittleWorkerModal = (deal) => {
    trackEvent('deals_little_worker_modal_opened', {
      source: 'diagnostics_page_dpe_button', // Source spécifique
      propertyId: selectedProperty?.id
    });
    setSelectedLittleWorkerDeal(deal); // deal sera { id: 'little-worker', provider: 'Little Worker', category: 'energie' }
    setOpenLittleWorkerModal(true);
    setAdemeNumber('');
    setAdemeNumberError('');
  };

  const handleCloseLittleWorkerModal = () => {
    trackEvent('deals_little_worker_cancelled', {
        source: 'diagnostics_page_dpe_button',
        propertyId: selectedProperty?.id
    });
    setOpenLittleWorkerModal(false);
    setSelectedLittleWorkerDeal(null);
    setAdemeNumber('');
    setAdemeNumberError('');
  };

  const handleConfirmLittleWorker = async () => {
    if (!ademeNumber.trim()) {
      setAdemeNumberError("Le numéro ADEME est requis.");
      return;
    }
    setAdemeNumberError('');

    if (selectedLittleWorkerDeal) {
      trackEvent('deals_little_worker_confirmed', {
        source: 'diagnostics_page_dpe_button',
        propertyId: selectedProperty?.id,
        dealId: selectedLittleWorkerDeal.id,
        ademeNumberProvided: ademeNumber.trim() !== ''
      });
      try {
        await axiosInstance.post('/deals/log-click', {
          dealId: selectedLittleWorkerDeal.id,
          provider: selectedLittleWorkerDeal.provider,
          category: selectedLittleWorkerDeal.category,
          source: 'diagnostics_modal_confirm_lw',
          ademeNumber: ademeNumber.trim(),
          propertyId: selectedProperty?.id
        });
        alert("Votre demande de simulation a été prise en compte et vous sera adressée sous 24h.");
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement du clic pour Little Worker:', error);
        alert("Une erreur est survenue lors de l'envoi de votre demande.");
      }
    }
    setOpenLittleWorkerModal(false);
    setSelectedLittleWorkerDeal(null);
    setAdemeNumber('');
  };

  // Trouver le technicien correspondant à la propriété sélectionnée
  const getTechnicianForProperty = (property) => {
    if (!property || !property.technicianId || !technicians) return null;
    return technicians.find(tech => tech.id === property.technicianId);
  };

  const handleCloseTipSuccessModal = () => {
    setIsTipSuccessModalOpen(false);
  };

  const handleCloseTipErrorModal = () => {
    setIsTipErrorModalOpen(false);
  };

  return (
    <Layout>
      <Helmet>
        <title>Diagnostics - Dimo Diagnostic</title>
        <meta name="description" content="Gérez vos dossier de diagnostics immobiliers" />
        <link rel="canonical" href={`https://client.dimo-diagnostic.net/diagnostics${id ? `/${id}` : ''}`} />
      </Helmet>

      {(propertiesLoading || isChangingProperty) ? (
        <LoadingSkeleton />
      ) : (
        <>
          {selectedProperty && (
            <DiagnosticsContent
              key={selectedProperty.id}
              propertyInfo={selectedProperty}
              onPrintClick={handleOpenPrintModal}
              onPayClick={handleOpenPaymentModal}
              onSendClick={handleOpenSendModal}
              onShareClick={handleOpenShareAccessModal}
              onDownloadClick={handleOpenDownloadModal}
              onHelpClick={handleNavigateToSupport}
              onCannotProvideDocument={handleCannotProvideDocument}
              isLoadingDocuments={isLoadingDocuments}
              hasReportDocuments={documents ? documents.some(doc => doc.type === 'report') : false}
              technician={selectedProperty ? getTechnicianForProperty(selectedProperty) : null}
              onTipClick={handleOpenTipModal}
              PropertySelectorComponent={
                properties.length > 1 ? (
                  <PropertySelector
                    selectedProperty={selectedProperty ? selectedProperty.id : null}
                    setSelectedProperty={handlePropertyChange}
                    properties={properties}
                    isLoading={false}
                  />
                ) : null
              }
              isUserPro={isPro}
              isUserIndependent={isIndependent}
              onOpenLittleWorkerModal={handleOpenLittleWorkerModal}
            />
          )}
          <PrintModal
            isOpen={isPrintModalOpen}
            onClose={() => {
              trackEvent('diagnostic_close_print_modal', { propertyId: selectedProperty?.id });
              setIsPrintModalOpen(false);
            }}
            onPrint={handlePrint}
            documents={documentsWithoutInvoice}
            isLoadingDocuments={isLoadingDocuments}
            dealId={selectedProperty ? selectedProperty.id : null}
            printAsked={selectedProperty ? selectedProperty.print : false}
          />
          <PaymentModal
            isOpen={isPaymentModalOpen}
            onClose={() => {
              trackEvent('diagnostic_close_payment_modal', { propertyId: selectedProperty?.id });
              setIsPaymentModalOpen(false);
            }}
            dealId={selectedProperty ? selectedProperty.id : null}
            paymentLink={selectedProperty ? selectedProperty.paymentLink : null}
            technician={selectedProperty ? getTechnicianForProperty(selectedProperty) : null}
            totalAmount={selectedProperty ? selectedProperty.amount : null}
          />
          <SendModal
            isOpen={isSendModalOpen}
            onClose={() => {
              trackEvent('diagnostic_close_send_modal', { propertyId: selectedProperty?.id });
              setIsSendModalOpen(false);
            }}
            onSend={handleSend}
            documents={documents}
            isLoadingDocuments={isLoadingDocuments}
            dealId={selectedProperty ? selectedProperty.id : null}
          />
          <ShareAccessModal
            isOpen={isShareAccessModalOpen}
            onClose={() => {
              trackEvent('diagnostic_close_share_modal', { propertyId: selectedProperty?.id });
              setIsShareAccessModalOpen(false);
            }}
            dealId={selectedProperty ? selectedProperty.id : null}
            onShareAccess={handleShareAccess}
          />
          <DownloadModal
            isOpen={isDownloadModalOpen}
            amount={selectedProperty ? selectedProperty.amount : null}
            onClose={() => {
              trackEvent('diagnostic_close_download_modal', { propertyId: selectedProperty?.id });
              setIsDownloadModalOpen(false);
            }}
            documents={documents}
            isLoadingDocuments={isLoadingDocuments}
            dealId={selectedProperty ? selectedProperty.id : null}
            canReview={selectedProperty?.actions?.review || false}
            technician={selectedProperty ? getTechnicianForProperty(selectedProperty) : null}
          />
          <TipModal
            isOpen={isTipModalOpen}
            onClose={() => setIsTipModalOpen(false)}
            dealId={selectedProperty ? selectedProperty.id : null}
            technician={selectedProperty ? getTechnicianForProperty(selectedProperty) : null}
            amount={selectedProperty ? selectedProperty.amount : null}
       />
          
          {/* Nouveaux modaux pour le succès/erreur de pourboire */}
          <TipSuccessModal
            isOpen={isTipSuccessModalOpen}
            onClose={handleCloseTipSuccessModal}
            dealId={selectedProperty ? selectedProperty.id : null}
            amount={tipAmount}
            technicien={selectedProperty ? getTechnicianForProperty(selectedProperty) : null}
          />
          <TipErrorModal
            isOpen={isTipErrorModalOpen}
            onClose={handleCloseTipErrorModal}
            dealId={selectedProperty ? selectedProperty.id : null}
          />

          {/* Modal de confirmation Little Worker */}
          <Dialog open={openLittleWorkerModal} handler={handleCloseLittleWorkerModal} size="md">
            <DialogHeader className="justify-center">
              <Typography variant="h5" className="text-center">
                🎉 Super choix !
              </Typography>
            </DialogHeader>
            <DialogBody className="text-center">
              <Typography className="mb-4 font-medium text-lg text-gray-800">
                Pour obtenir votre simulation énergétique offerte, il vous suffit de mentionner ci-dessous votre numéro ADEME délivré par votre rapport de diagnostic immobilier.
              </Typography>
              <Typography className="mb-6 text-gray-600">
                Votre rapport vous sera adressé sous 24h après réception de votre demande.{' '}
                <a 
                  href="/little-worker.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-dimo-primary hover:text-dimo-primary-dark font-medium underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Voir un exemple de rapport
                </a>
              </Typography>
              
              <div className="mb-4 px-8">
                <Input 
                  type="text"
                  label="Numéro ADEME de votre diagnostic"
                  value={ademeNumber}
                  onChange={(e) => {
                    setAdemeNumber(e.target.value);
                    if (ademeNumberError) setAdemeNumberError('');
                  }}
                  error={!!ademeNumberError}
                />
                {ademeNumberError && (
                  <Typography color="red" variant="small" className="mt-1 text-left">
                    {ademeNumberError}
                  </Typography>
                )}
              </div>

              <div className="mt-8 mb-4 px-8 text-xs text-gray-600 text-left">
                <span className="font-bold">Obtenez gratuitement :</span>
                <ul className="list-disc pl-5 mt-1">
                  <li>Les forces et faiblesses de votre logement avec un schéma de déperditions.</li>
                  <li>Des scénarios de rénovation pour améliorer votre note DPE.</li>
                  <li>Des devis gratuits avec estimations des coûts des travaux pour chaque scénario.</li>
                  <li>Une simulation d'économies réalisées par scénario.</li>
                  <li>Les bénéfices annuels générés par une rénovation.</li>
                </ul>
              </div>
              
              <div className="flex gap-4 justify-center mb-6 mt-8">
                <MaterialButton
                  variant="outlined"
                  onClick={handleCloseLittleWorkerModal}
                  style={{ borderColor: colors.dimoPrimary, color: colors.dimoPrimary }}
                >
                  Plus tard
                </MaterialButton>
                <MaterialButton
                  onClick={handleConfirmLittleWorker}
                  style={{ backgroundColor: colors.dimoPrimary }}
                >
                  Obtenir ma simulation offerte
                </MaterialButton>
              </div>
              <Typography variant="small" className="text-gray-500 text-xs">
                En cliquant sur "Obtenir ma simulation offerte", vous reconnaissez que vos données personnelles seront traitées conformément à la{' '}
                <a 
                  href="https://www.dimo-diagnostic.net/protection-des-donnees" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  politique de confidentialité de DIMO DIAGNOSTIC
                </a>
                .
              </Typography>
            </DialogBody>
          </Dialog>

          {properties.length === 0 && !propertiesLoading && (
            <Card className="mb-6 shadow-md">
              <div className="p-6 text-center">
                <p className="text-red-500">Aucun dossier n'est actuellement disponible pour votre compte.</p>
              </div>
            </Card>
          )}
        </>
      )}
    </Layout>
  );
}

